import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { useTable } from "../../context/TableContext.js";
const EditableCell = ({
  cell,
  isHeader,
  isFooter,
  width,
  height,
  isFirst,
  last,
}) => {
  const { state, actions } = useTable();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [caretBox, setCaretBox] = useState(null);
  const [pos, setPos] = useState({ x: 8, y: 28 }); 
  const popRef = useRef(null);
  const [active, setActive] = useState(0);
  const contentRef = useRef(null);
  const Tag = isHeader ? "th" : "td";
  const isSelected = state.selectedCell?.id === cell.id;
  const labels = state.labels || [];
    const replaceHashtagWithLabel = (labelText) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !contentRef.current) return;

    const range = sel.getRangeAt(0);
    const full = contentRef.current.textContent || "";
    const preR = range.cloneRange();
    preR.selectNodeContents(contentRef.current);
    preR.setEnd(range.endContainer, range.endOffset);
    const before = preR.toString();

    const match = before.match(/(^|\s)#([^\s]*)$/);
    if (!match) return;

    const tagLen = match[0].length;
    const tagStart = before.length - tagLen;
    const after = full.slice(before.length);

    const insert = `${match[1]}#${labelText} `;
    const newContent = before.slice(0, tagStart) + insert + after;

    contentRef.current.textContent = newContent;
    actions.updateCellContent(cell.id, newContent);
    actions.applyLabelToCell(cell.id, labelText);

    const node = contentRef.current.firstChild;
    if (node && node.nodeType === Node.TEXT_NODE) {
      const caretPos = (before.slice(0, tagStart) + insert).length;
      const newRange = document.createRange();
      newRange.setStart(node, Math.min(caretPos, node.textContent.length));
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }

    setPickerOpen(false);
    setQuery("");
    };
  
  useEffect(() => {
    if (!isSelected) {
      // selection moved to another cell → close and clear this one
      setPickerOpen(false);
    }
  }, [isSelected, cell.id, actions]);
  const filtered = useMemo(() => {
      const q = (query || "").toLowerCase();
      if (!q) return labels;
      return labels.filter((l) => l.toLowerCase().startsWith(q));
    }, [labels, query]);
  const handleClick = (e) => {
    e.stopPropagation();
    actions.selectCell(cell);
  };
  const updateCaretAndMaybeShowPicker = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      setPickerOpen(false);
      return;
    }
    const range = sel.getRangeAt(0);

    if (
      !contentRef.current ||
      !contentRef.current.contains(range.endContainer)
    ) {
      setPickerOpen(false);
      return;
    }
    // text before caret
    const pre = range.cloneRange();
    pre.selectNodeContents(contentRef.current);
    pre.setEnd(range.endContainer, range.endOffset);
    const before = pre.toString();

    const match = before.match(/(^|\s)#([^\s]*)$/);
    if (!match) {
      setPickerOpen(false);
      return;
    }
    setQuery(match[2] || "");
    
    const caretRect = range.getBoundingClientRect();
    setCaretBox(caretRect);

    setActive(0);
    setPickerOpen(true);
  };
  const handleContentChange = (e) => {
    const newContent = e.target.textContent || "";
    if (newContent !== (cell.content ?? "")) {
      actions.updateCellContent(cell.id, newContent);
    }
    updateCaretAndMaybeShowPicker();
  };
  const getCellStyles = () => {
    const styles = cell.styles || {};
    const tAlign =
      styles.textAlign === "center" ? "center" :
      styles.textAlign === "right"  ? "right"  : "left";
    const VAlign =
      styles.verticalAlign === "top" ? "top" :
      styles.verticalAlign === "bottom"  ? "bottom"  : "middle";
    const fallbackPct = 100 / Math.max(1, state.columns - 1);
    const colIndex = cell.col;
    const widthPct =
      state.columnWidths?.[colIndex] ?? styles.widthPercent ?? fallbackPct;

    const fontSizePx = styles.fontSize ?? 20;

    return {
      fontWeight: styles.fontWeight || "normal",
      fontStyle: styles.fontStyle || "normal",
      textDecoration: styles.textDecoration || "none",
      backgroundColor: styles.backgroundColor || "#ffffff",
      color: styles.textColor || "#000000",
      textAlign: tAlign,
      fontSize: `${fontSizePx}px`,
      lineHeight: styles.lineHeight ?? `${Math.round(fontSizePx * 1.25)}px`,
      whiteSpace: "normal",
      wordBreak: "break-all",          
      overflowWrap: "anywhere",       
      verticalAlign: VAlign,
      border: "0.5px solid rgba(209,213,219,1)",
      outline: isSelected ? "2px solid #3b82f6" : "none",
      outlineOffset: "-2px",
      width: `${widthPct}%`,
      height:`${100/31}%`
    };
  };

  return (
    <Tag
      ref={contentRef}
      className={`${isHeader ? "font-semibold" : ""} ${isFooter ? "font-semibold" : ""} break-words`}
      onClick={(e) => {
        handleClick(e);
        if (contentRef.current) {
          const rect = contentRef.current.getBoundingClientRect();
          let x = rect.right;
          let y = rect.bottom;

          const popupSize = 500; 
          if (x + popupSize > window.innerWidth) {
            x = rect.right - popupSize;
          }
          if (y + popupSize > window.innerHeight) {
            y = rect.bottom - popupSize;
          }

          setPos({ x, y });
        }
      }}
      colSpan={cell.colspan || 1}
      rowSpan={cell.rowspan || 1}
      data-testid={`cell-${cell.id}`}
      style={getCellStyles()}
      contentEditable
      suppressContentEditableWarning
      onInput={handleContentChange}
      
    >
    {cell.productTable ? (
Array.isArray(cell.columns) && cell.columns.length > 0 ? (
    <table
      // Let columns grow from their content (no fixed widths)
      style={{
        width: '100%',
        height:'100%',
        tableLayout: 'auto',
        borderCollapse: 'collapse',
        borderSpacing: 0,
      }}
    >
      {/* Remove colgroup widths – they pin columns and cause early wrapping */}
      <thead>
        <tr>
          {cell.columns.map((c, i) => (
            <th
              key={i}
              style={{
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                textAlign: 'left',
                fontWeight: 600,
                whiteSpace: 'nowrap',  // headers don’t wrap
              }}
              title={c.col_name}
            >
              {c.col_name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {cell.columns.map((_, i) => (
            <td
              key={i}
              style={{
                border: '1px solid #e5e7eb',
                verticalAlign: 'top',
              }}
            >
              Data
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  ) : (
    <div className="text-667085 text-xs">Configure columns…</div>
  )

      ) : ("Add")}
    {pickerOpen &&
        filtered.length > 0 &&
        createPortal(
          <div
            ref={popRef}
            className="fixed z-[9999] rounded-xl border border-gray-200 bg-white shadow-2xl"
            style={{
              left: pos.x,
              top: pos.y,
              width: "max-content",
              maxWidth: "min(60vw, 480px)",
              maxHeight: "min(50vh, 360px)",
              overflowY: "auto",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="py-1">
              {filtered.map((name, i) => (
                <button
                  key={name}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    replaceHashtagWithLabel(name);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full text-left px-3 py-2 text-sm ${
                    i === active
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-800"
                  }`}
                >
                  <span className="font-medium">#{name}</span>
                </button>
              ))}
            </div>
            <div className="px-3 py-1 border-t text-[11px] text-gray-500">
              Type to filter • Enter to select
            </div>
          </div>,
          document.body
        )}
    </Tag>
  );
}

export default EditableCell;